import { client } from '@studio/api/api-client/client';
import { USER_TYPE } from '@studio/constants/authentication';
import { CENTRAL_API_V2, STUDIO_URL } from '@studio/constants/env-variables';
import { TrackEventProp, TrackOrgProp, TrackUserProp } from '@studio/constants/tracking-constants';
import type { Authentication } from '@studio/types';
import * as SessionStore from '@studio/storeV2/authentication/session-store';
import { WrongUserTypeError } from '@studio/types';
import type { ApiClientConfig } from '@studio/types/axios';
import md5 from 'md5';
import { performLogout } from '../perform-logout';

const PRIVATE_ME_URL = `${CENTRAL_API_V2}/users/me`;
const PRIVATE_GRAVATAR_URL = 'https://www.gravatar.com/avatar';
const PRIVATE_ENCODED_DEFAULT_AVATAR_URL = encodeURIComponent(`${STUDIO_URL}/profile-avatar.jpg`);

const buildUserImage = (userName: string): string =>
  `${PRIVATE_GRAVATAR_URL}/${md5(userName.trim().toLowerCase())}?d=${PRIVATE_ENCODED_DEFAULT_AVATAR_URL}`;

/**
 * GET `/users/me`, reject SITE users, fire pendo/analytics, store the user in the session.
 * On failure it forces logout and rethrows so the calling flow stops.
 */
export default async function fetchMe(): Promise<void> {
  try {
    const config: ApiClientConfig = { token: SessionStore.session.getAccessToken() };
    const response: Authentication.Get.Me = await client(PRIVATE_ME_URL, config);
    const { capabilities, customerId, customerName, firstName, lastName, userId, userName, userType } = response;

    if (userType === USER_TYPE.SITE) {
      throw new WrongUserTypeError('unsupported user type');
    }

    pendo.initialize({
      // @ts-expect-error firstName/lastName are optional; pendo tolerates undefined at runtime
      visitor: { id: userId, username: userName, first_name: firstName, last_name: lastName, capabilities },
      account: { id: customerId, org_name: customerName },
    });

    window.analytics.identify(userId, {
      [TrackUserProp.USER_NAME]: userName,
      [TrackOrgProp.ORG_NAME]: customerName,
      [TrackOrgProp.ORG_UUID]: customerId,
      [TrackUserProp.USER_PERMISSIONS]: capabilities,
      [TrackEventProp.STUDIO_VERSION]: import.meta.env.VITE_APP_VERSION,
    });

    window.analytics.group(customerId, {
      [TrackOrgProp.ORG_NAME]: customerName,
      [TrackOrgProp.ORG_UUID]: customerId,
    });

    SessionStore.session.setUser({
      ...response,
      userImage: buildUserImage(response.userName),
    });
  } catch (err) {
    if (err instanceof WrongUserTypeError) {
      throw err;
    }
    if (err instanceof Error) {
      console.error(err);
      await performLogout();
    }
    throw err;
  }
}
